import { API_BASE_URL } from "./config";
import { authHeadersForGet, authHeadersJson } from "./http";
import {
  Sale,
  CreateSaleDto,
  CreatePaymentDto,
  UpdateSaleDto,
} from "../types/sale";

export interface SalesResponse {
  data: Sale[];
  total: number;
  page: number;
  lastPage: number;
}

export interface SalesSearchParams {
  page?: number;
  limit?: number;
  clientReference?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export const salesApi = {
  getAll: async (
    page: number = 1,
    limit: number = 10,
    filters?: Partial<SalesSearchParams>
  ): Promise<SalesResponse> => {
    const queryParams = [`page=${page}`, `limit=${limit}`];

    if (filters) {
      if (filters.clientReference) queryParams.push(`clientReference=${filters.clientReference}`);
      if (filters.status) queryParams.push(`status=${filters.status}`);
      if (filters.startDate) queryParams.push(`startDate=${filters.startDate}`);
      if (filters.endDate) queryParams.push(`endDate=${filters.endDate}`);
    }

    const response = await fetch(
      `${API_BASE_URL}/sales?${queryParams.join("&")}`,
      { headers: authHeadersForGet() }
    );
    return response.json();
  },

  getOne: async (reference: string): Promise<Sale> => {
    const response = await fetch(`${API_BASE_URL}/sales/${reference}`, {
      headers: authHeadersForGet(),
    });
    return response.json();
  },

  create: async (saleData: CreateSaleDto): Promise<Sale> => {
    const response = await fetch(`${API_BASE_URL}/sales`, {
      method: "POST",
      headers: authHeadersJson(),
      body: JSON.stringify(saleData),
    });
    return response.json();
  },

  createPayment: async (
    saleReference: string,
    body: CreatePaymentDto
  ): Promise<Sale> => {
    const response = await fetch(`${API_BASE_URL}/sales/${saleReference}/payments`, {
      method: "POST",
      headers: authHeadersJson(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Error al crear el pago (${response.status})`);
    }
    return response.json();
  },

  getByClientReference: async (clientReference: string): Promise<Sale[]> => {
    const response = await fetch(`${API_BASE_URL}/sales/client/${clientReference}`, {
      headers: authHeadersForGet(),
    });
    return response.json();
  },

  getPending: async (): Promise<Sale[]> => {
    const response = await fetch(`${API_BASE_URL}/sales/pending`, {
      headers: authHeadersForGet(),
    });
    return response.json();
  },

  getPendingWithConversations: async (): Promise<{
    sales: Sale[];
    conversations: {
      saleReference: string;
      clientReference: string;
      clientName: string;
      text: string;
    }[];
  }> => {
    const response = await fetch(`${API_BASE_URL}/sales/pending/conversations`, {
      headers: authHeadersForGet(),
    });
    return response.json();
  },

  delete: async (reference: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/sales/${reference}`, {
      method: "DELETE",
      headers: authHeadersForGet(),
    });
    if (!response.ok && response.status !== 204) {
      const text = await response.text();
      throw new Error(text || `Error al eliminar la venta (${response.status})`);
    }
    return true;
  },

  update: async (
    reference: string,
    updateData: Partial<CreateSaleDto> & Partial<UpdateSaleDto>
  ): Promise<Sale> => {
    const response = await fetch(`${API_BASE_URL}/sales/${reference}`, {
      method: "PUT",
      headers: authHeadersJson(),
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(`Error al actualizar la venta: ${response.statusText}`);
    }

    return response.json();
  },
};
